import { useSessionContext } from '@/contexts/session/context';
import { safeJson } from '@/scripts/utils';
import { useRefs } from '@/scripts/hooks';
import { isInitial } from '@/configs/apis/user';
import type {
  MediaCallParams,
  ChatMediaInfo,
  ChatMediaInfoPartial,
} from '../hooks/useChatMediaCall/types';
import {
  CallOperateCode,
  ChatMediaType,
  type MediaCallOperateParams,
} from '../hooks/useChatMediaCall/types';
import type {
  ChatMessagesArgMessageIdParams,
  ChatMessagePayloadType,
} from '../hooks/useChatMessages/types';
import type { ChatContextHooksFull, ChatContextHooksProps } from '../../context';
import { eachMessagesArgs } from '../hooks/useChatMessages/utils';

export type UseChatMediaSingleValueResult = ReturnType<typeof useChatMediaSingleValue>;

export function useChatMediaSingleValue({ hooks }: ChatContextHooksProps) {
  const {
    session: { user },
  } = useSessionContext();

  const { chats, chatMessages, chatMediaCall, chatMediaQuickVO } = hooks;

  const { quitRoomEx, roomUsers } = chatMediaQuickVO;

  const rooUsersRef = useRef(roomUsers);

  const mediaOperateOfCallSingle = useCallback(
    async (params: MediaCallOperateParams) => {
      const { chatMediaValue } = hooks as ChatContextHooksFull;
      const chat = await chats.getChat(params);
      if (chat && user) {
        await chatMediaValue
          .mediaOperateOfCall({
            ...params,
            type: ChatMediaType.SINGLE,
            callMembers: [chat.user],
          })
          .catch((err) => {
            if (err?.code === CallOperateCode.RECEIVE_NO_FRIEND) {
              message.error('The other party refused to call');
            }
            throw err;
          });
      }
    },
    [chats, hooks, user]
  );

  const mediaCallSingle = useCallback(
    async (params: MediaCallParams) => {
      const { chatMediaValue } = hooks as ChatContextHooksFull;
      chatMediaCall.activeChatMedia(params.chat_id);
      await chatMediaValue.mediaCall(params, {
        mediaOperateOfCall: mediaOperateOfCallSingle,
      });
    },
    [chatMediaCall, hooks, mediaOperateOfCallSingle]
  );

  const mediaNoticeSingle = useCallback(
    async (params: ChatMessagesArgMessageIdParams[]) => {
      if (isInitial) {
        await eachMessagesArgs(params, async (msg, chat_id) => {
          const payload = safeJson(msg.payload!, { room_id: '', busy: 0 });
          if (payload.room_id && payload.busy === ChatMediaType.SINGLE) {
            const { chatMediaValue } = hooks as ChatContextHooksFull;
            const callingInfo = await chatMediaValue.getMediaCallingInfoFull({
              room_id: payload.room_id,
            });

            if (callingInfo) {
              const chat = await chats.getChat({ chat_id });
              if (chat) {
                const localMsg = await chatMessages.getMessage(chat_id, msg);

                if (!chatMediaCall.chatMediaInfo) {
                  chatMediaCall.activeChatMedia(chat_id);
                }

                chatMediaCall.setChatMediaInfo({
                  [chat_id]: {
                    ...callingInfo,
                    chat,
                    room_id: payload.room_id,
                    message_id: msg.message_id,
                    cli_msg_id: localMsg?.cli_msg_id || msg.cli_msg_id,
                    payload_type: msg.payload_type as ChatMessagePayloadType,
                    owner_id: msg.owner_id,
                  },
                });
              }
            }
          }
        });
      }
    },
    [chatMediaCall, chatMessages, chats, hooks]
  );

  const mediaStatusUpdateSingle = useCallback(
    async (params: ChatMessagesArgMessageIdParams[]) => {
      if (isInitial) {
        await eachMessagesArgs(params, async (msg, chat_id) => {
          const { chatMediaValue } = hooks as ChatContextHooksFull;
          const chatMediaInfo = await chatMediaCall.getChatMediaInfo(chat_id);

          if (chatMediaInfo) {
            const { message_id, cli_msg_id } = chatMediaInfo;

            if (msg.message_id === message_id || msg.cli_msg_id === cli_msg_id) {
              const mediaInfo: ChatMediaInfoPartial = {};

              if (!message_id) {
                mediaInfo.message_id = msg.message_id;
              }

              if (msg.status) {
                mediaInfo.status = msg.status;
              }

              if (msg.owner_id) {
                mediaInfo.owner_id = msg.owner_id;
              }

              chatMediaCall.setChatMediaInfo({ [chat_id]: mediaInfo });
            }

            if (chatMediaCall.isMediaPayloadTypeEnd(msg.status!)) {
              chatMediaValue.mediaEndCall({
                chat: { chat_id },
                ...msg,
              } as ChatMediaInfo);
            }
          }
        });
      }
    },
    [chatMediaCall, hooks]
  );

  const refs = useRefs({
    chatMediaCall,
    quitRoomEx,
    user,
    chatMessages,
    hooks: hooks as ChatContextHooksFull,
  });

  // Handle user change
  useEffect(() => {
    const {
      hooks: { chatMediaValue, chatMediaCall },
    } = refs.current;
    if (
      chatMediaCall.chatMediaInfo?.type === ChatMediaType.SINGLE &&
      chatMediaValue.mediaIsReady
    ) {
      if (roomUsers.length < rooUsersRef.current.length && roomUsers.length < 2) {
        chatMediaValue.mediaHangUp();
        rooUsersRef.current = [];
      } else {
        rooUsersRef.current = roomUsers;
      }
    }
  }, [refs, roomUsers]);

  return useMemo(
    () => ({
      mediaCallSingle,
      mediaNoticeSingle,
      mediaStatusUpdateSingle,
    }),
    [mediaCallSingle, mediaNoticeSingle, mediaStatusUpdateSingle]
  );
}
